import React from 'react';
import styled from 'styled-components';
import { Container, SectionTitle, Grid } from '../../styles/components.js';
import { webProjects } from '../../constants/data.js';
import { Link } from 'react-router-dom';

const PortfolioSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const SectionDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.body.large};
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 0;
  line-height: 1.7;
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const PortfolioGrid = styled(Grid)`
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProjectCard = styled.div`
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.card};
  height: 400px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 30px 60px ${({ $themeColor }) => $themeColor ? `${$themeColor}20` : 'rgba(0,0,0,0.25)'};
    border-color: ${({ $themeColor }) => $themeColor ? `${$themeColor}40` : 'transparent'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 320px;
  }
`;

const ImageContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(3, 3, 3, 0.9) 0%, rgba(3, 3, 3, 0.4) 50%, rgba(3, 3, 3, 0.1) 100%);
    z-index: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transition: transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);

    ${ProjectCard}:hover & {
      transform: scale(1.04);
    }
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 2.25rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CardCategory = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $themeColor, theme }) => $themeColor || theme.colors.primary};
  background-color: ${({ $themeColor }) => $themeColor ? `${$themeColor}12` : 'rgba(255,255,255,0.08)'};
  padding: 4px 10px;
  border-radius: 6px;
  width: fit-content;
`;

const CardTitle = styled.h3`
  font-size: clamp(1.3rem, 2.5vw, 1.7rem);
  font-weight: 900;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.03em;
`;

const TechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 0.25rem;
`;

const MiniTag = styled.span`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  padding: 4px 10px;
  border-radius: 99px;
  font-weight: 600;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
`;

const DetailsLink = styled(Link)`
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    color: ${({ $themeColor, theme }) => $themeColor || theme.colors.primary};
    svg {
      transform: translateX(4px);
    }
  }
`;

const Portfolio = () => {
  return (
    <PortfolioSection id="projects">
      <Container>
        <SectionHeader>
          <Label>04 / WEB PROJECTS</Label>
          <SectionTitle>Web Projects</SectionTitle>
          <SectionDescription>
            Explore my latest web development projects and discover how I create powerful, user-friendly digital experiences.
          </SectionDescription>
        </SectionHeader>

        <PortfolioGrid>
          {webProjects.map((item) => (
            <ProjectCard key={item.id} $themeColor={item.color}>
              <ImageContainer>
                <img src={item.image} alt={item.title} />
              </ImageContainer>
              <CardContent>
                <CardCategory $themeColor={item.color}>{item.category}</CardCategory>
                <CardTitle>{item.title}</CardTitle>
                <TechRow>
                  {item.tags.slice(0, 4).map((tag, idx) => (
                    <MiniTag key={idx}>{tag}</MiniTag>
                  ))}
                  {item.tags.length > 4 && <MiniTag>+{item.tags.length - 4}</MiniTag>}
                </TechRow>
                <ActionRow>
                  <DetailsLink to={`/webProjects/${item.id}`} $themeColor={item.color}>
                    View Case Study{' '}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </DetailsLink>
                </ActionRow>
              </CardContent>
            </ProjectCard>
          ))}
        </PortfolioGrid>
      </Container>
    </PortfolioSection>
  );
};

export default Portfolio;
